/**
 * Client HTTP NORYVOX.
 *
 * Responsabilités :
 * - instance Axios unique (`baseURL` depuis `VITE_API_URL`, repli sur `API_BASE_PATH`) ;
 * - injection de l'en-tête `Authorization` et d'un `X-Request-Id` de corrélation ;
 * - déroulage de l'enveloppe `{ data, meta }` en intercepteur de réponse ;
 * - normalisation de `{ error }` en erreur typée `ApiError` ;
 * - rafraîchissement automatique sur `401` avec file d'attente des requêtes concurrentes.
 *
 * Ce module ne dépend d'aucun état applicatif : les accès session lui sont fournis via
 * `setApiAuthHandlers`, ce qui évite tout cycle d'import avec `auth/session.ts`.
 */

import {
  API_BASE_PATH,
  API_ERROR_CODES,
  API_HEADERS,
  AUTH_ROUTES,
  type ApiErrorBody,
  type ApiResponse,
  type AuthSessionResponse,
  type FieldError,
} from '@noryvox/shared';
import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

/** Délai maximal d'une requête avant abandon. */
const REQUEST_TIMEOUT_MS = 20_000;

/** Base d'URL résolue une fois au chargement du module. */
const configuredBaseUrl = import.meta.env.VITE_API_URL;
export const API_BASE_URL: string =
  configuredBaseUrl !== undefined && configuredBaseUrl.length > 0
    ? configuredBaseUrl
    : API_BASE_PATH;

/** Erreur applicative normalisée, indépendante du transport. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  readonly requestId: string | null;
  readonly fields: FieldError[];

  constructor(params: {
    status: number;
    code: string;
    message: string;
    details?: unknown;
    requestId?: string | null;
    fields?: FieldError[];
  }) {
    super(params.message);
    this.name = 'ApiError';
    this.status = params.status;
    this.code = params.code;
    this.details = params.details;
    this.requestId = params.requestId ?? null;
    this.fields = params.fields ?? [];
  }
}

/** Indique si une valeur est une erreur d'API normalisée. */
export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

/** Retourne le code métier d'une erreur, ou `null`. */
export function getApiErrorCode(error: unknown): string | null {
  return error instanceof ApiError ? error.code : null;
}

/** Contrat d'accès à la session, branché par `auth/session.ts`. */
export interface ApiAuthHandlers {
  getAccessToken: () => string | null;
  getRefreshToken: () => string | null;
  applySession: (session: AuthSessionResponse) => void;
  onSessionExpired: () => void;
}

let authHandlers: ApiAuthHandlers | null = null;

/** Branche la couche session sur le client HTTP. */
export function setApiAuthHandlers(handlers: ApiAuthHandlers): void {
  authHandlers = handlers;
}

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

function createRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `req-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function extractFieldErrors(details: unknown): FieldError[] {
  if (details === null || typeof details !== 'object') {
    return [];
  }
  const errors = (details as { errors?: unknown }).errors;
  if (!Array.isArray(errors)) {
    return [];
  }
  return errors.filter((item): item is FieldError => {
    if (item === null || typeof item !== 'object') {
      return false;
    }
    return typeof (item as FieldError).message === 'string';
  });
}

function readHeader(response: AxiosResponse | undefined, name: string): string | null {
  if (response === undefined) {
    return null;
  }
  const value =
    (response.headers as Record<string, unknown>)[name] ??
    (response.headers as Record<string, unknown>)[name.toLowerCase()];
  return typeof value === 'string' ? value : null;
}

/** Convertit une erreur Axios en `ApiError` typée. */
function toApiError(error: AxiosError<ApiErrorBody>): ApiError {
  const response = error.response;
  const body = response?.data;
  const normalized =
    body !== undefined && typeof body === 'object' && 'error' in body ? body.error : undefined;
  const status = response?.status ?? 0;

  return new ApiError({
    status,
    code: normalized?.code ?? (status === 0 ? 'NETWORK_ERROR' : API_ERROR_CODES.internalError),
    message:
      normalized?.message ??
      error.message ??
      'Une erreur inattendue est survenue. Réessayez dans quelques instants.',
    details: normalized?.details,
    requestId: normalized?.requestId ?? readHeader(response, API_HEADERS.requestId),
    fields: extractFieldErrors(normalized?.details),
  });
}

function toUnexpectedApiError(error: unknown): ApiError {
  return new ApiError({
    status: 0,
    code: 'UNEXPECTED_ERROR',
    message:
      error instanceof Error && error.message.length > 0
        ? error.message
        : 'Une erreur inattendue est survenue. Réessayez dans quelques instants.',
  });
}

/** Instance principale, protégée par les intercepteurs. */
const http: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: REQUEST_TIMEOUT_MS,
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
});

/** Instance nue réservée au rafraîchissement (aucun intercepteur, pas de récursion). */
const refreshHttp: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: REQUEST_TIMEOUT_MS,
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
});

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = authHandlers?.getAccessToken() ?? null;
  if (token !== null && !config.headers.has(API_HEADERS.authorization)) {
    config.headers.set(API_HEADERS.authorization, `Bearer ${token}`);
  }
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    config.headers.delete('Content-Type');
  }
  if (!config.headers.has(API_HEADERS.requestId)) {
    config.headers.set(API_HEADERS.requestId, createRequestId());
  }
  return config;
});

/** Déroule l'enveloppe `{ data }` : la charge utile remplace `response.data`. */
function unwrapEnvelope(response: AxiosResponse): AxiosResponse {
  if (response.status === 204) {
    response.data = undefined;
    return response;
  }
  const body = response.data as unknown;
  if (body !== null && typeof body === 'object' && 'data' in body) {
    response.data = (body as ApiResponse<unknown>).data;
  }
  return response;
}

let refreshInFlight: Promise<string | null> | null = null;

/** Rafraîchit le jeton d'accès ; les appels concurrents partagent la même promesse. */
function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight === null) {
    refreshInFlight = performRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

async function performRefresh(): Promise<string | null> {
  const handlers = authHandlers;
  const refreshToken = handlers?.getRefreshToken() ?? null;
  if (handlers === null || refreshToken === null) {
    return null;
  }
  try {
    const response = await refreshHttp.post<ApiResponse<AuthSessionResponse>>(AUTH_ROUTES.refresh, {
      refreshToken,
    });
    const session = response.data.data;
    handlers.applySession(session);
    return session.accessToken;
  } catch {
    handlers.onSessionExpired();
    return null;
  }
}

function shouldAttemptRefresh(
  error: AxiosError<ApiErrorBody>,
  config: RetriableConfig | undefined,
): boolean {
  if (config === undefined || error.response?.status !== 401) {
    return false;
  }
  if (config._retry === true || authHandlers === null || authHandlers.getRefreshToken() === null) {
    return false;
  }
  const code = error.response.data?.error?.code;
  if (code === API_ERROR_CODES.authInvalidCredentials) {
    return false;
  }
  const url = config.url ?? '';
  return !url.endsWith(AUTH_ROUTES.refresh) && !url.endsWith(AUTH_ROUTES.login);
}

async function handleResponseError(error: unknown): Promise<AxiosResponse> {
  if (!axios.isAxiosError<ApiErrorBody>(error)) {
    throw toUnexpectedApiError(error);
  }

  const axiosError = error as AxiosError<ApiErrorBody>;
  const config = axiosError.config as RetriableConfig | undefined;

  if (shouldAttemptRefresh(axiosError, config)) {
    const token = await refreshAccessToken();
    if (token !== null && config !== undefined) {
      config._retry = true;
      config.headers.set(API_HEADERS.authorization, `Bearer ${token}`);
      return http.request(config);
    }
  }

  throw toApiError(axiosError);
}

http.interceptors.response.use(unwrapEnvelope, handleResponseError);

/**
 * Exécute une requête et retourne directement la charge utile déroulée de l'enveloppe `{ data }`.
 */
export async function apiRequest<T>(config: AxiosRequestConfig): Promise<T> {
  const response = await http.request<T>(config);
  return response.data;
}

export default http;
